import React, { useMemo } from "react";
import { Box, Skeleton, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { LoadingOverlay } from "../../common/LoadingOverlay";
import UnavailablePanel from "../../common/UnavailablePanel";
import { type LamGrouping } from "../../common/GroupSelector";
import { type RollBucket } from "../../../utils/rollBuckets";
import {
  AXIS_WIDTH,
  CHART_MARGIN,
  ROLL_COLORS,
  fmtRolls,
  renderShiftTick,
} from "../Lamination2/Lam2RollsChart";

/**
 * Roll grades as a share of output, for either lamination line.
 *
 * The bar card next to this one shows volume, which swamps quality: a bad
 * period that ran fewer rolls looks smaller, not worse. Plotting the mix as
 * percentages takes volume out, so a drift in A/B/F is visible on its own.
 *
 * The PLC counts rolls per shift, so shifts are what the API returns and what
 * gets summed into whichever bucket is on screen. Grouping by day puts the
 * three shifts of a day on one point, which is the comparison the overview
 * wants; by week, a fortnight of days that individually look noisy.
 */

type Point = {
  shift_id: number;
  label: string;
  sublabel: string;
  is_partial: boolean;
  total: number;
  /** Percentages — null for a shift that produced nothing, so the line breaks. */
  a: number | null;
  b: number | null;
  f: number | null;
  counts: { a: number; b: number; f: number };
};

/** Target share of A-grade rolls, drawn as a fixed reference on the chart. */
const GOAL_A_PCT = 95;

/**
 * The level A-grade share should not fall below. Drawn only — nothing fires on
 * it yet; the alert itself is separate work.
 */
const ALERT_A_PCT = 80;

const pct = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : null);

/**
 * One point per bucket, oldest first.
 *
 * A bucket is partial when it holds the shift still running, or when it is the
 * current day/week — either way it has not finished, so the last point is not
 * yet comparable with the ones before it.
 */
const toPoints = (buckets: RollBucket[]): Point[] =>
  buckets.map((b) => ({
  // The x axis keys on this; a bucket start is unique and sorts right.
    shift_id: b.key,
    label: b.label,
    sublabel: b.sublabel,
    is_partial: b.is_partial,
    total: b.total,
    a: pct(b.a, b.total),
    b: pct(b.b, b.total),
    f: pct(b.f, b.total),
    counts: { a: b.a, b: b.b, f: b.f },
  }));

/**
 * The same rolls as the panel above, as percentages, sharing its shifts.
 *
 * Both panels draw the card's one bucket list and key on the bucket start, so
 * a period sits at the same x in each. See AxisAlign.test.tsx.
 */
type Props = {
  /** The same buckets the counts panel draws. */
  buckets: RollBucket[];
  group: LamGrouping;
  loading: boolean;
  /** Why there is nothing real to show; null when there is. */
  unavailable: string | null;
};

const RollMixPanel: React.FC<Props> = ({ buckets, group, loading, unavailable }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const data = useMemo(() => toPoints(buckets), [buckets]);

  // Window averages: totals first, so a small bucket can't swing the average
  // the way a mean-of-percentages would.
  const avg = useMemo(() => {
    const t = data.reduce(
      (acc, p) => {
        acc.a += p.counts.a;
        acc.b += p.counts.b;
        acc.f += p.counts.f;
        return acc;
      },
      { a: 0, b: 0, f: 0 }
    );
    const all = t.a + t.b + t.f;
    return { a: pct(t.a, all), b: pct(t.b, all), f: pct(t.f, all) };
  }, [data]);

  const partial = useMemo(() => data.find((p) => p.is_partial), [data]);

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Quality mix
      </Typography>

      {unavailable ? (
        <UnavailablePanel reason={unavailable}>
            <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
              <ComposedChart data={data} margin={CHART_MARGIN}>
                <Bar dataKey="__axisSpacer" hide />
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="shift_id" tick={false} height={42} />
                <YAxis width={AXIS_WIDTH} domain={[0, 100]} tick={false} />
                <Line type="linear" dataKey="a" stroke={ROLL_COLORS.a} strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="linear" dataKey="b" stroke={ROLL_COLORS.b} strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="linear" dataKey="f" stroke={ROLL_COLORS.f} strokeWidth={2} dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </UnavailablePanel>
        ) : (
          <LoadingOverlay
            show={loading}
            minHeight={isMobile ? 220 : 300}
            label="Loading quality mix…"
          >
            {data.every((p) => p.total === 0) && !loading ? (
              <Typography variant="body2" color="text.secondary">
                No roll counts recorded yet for this line.
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                <ComposedChart data={data} margin={CHART_MARGIN}>
                  {/*
                    A plain LineChart puts the first point hard against the axis
                    (recharts picks a "point" scale) rather than centering it in a
                    band. A hidden bar switches this chart to the band scale, so
                    points sit in the middle of the period they describe — a day's
                    mix belongs across that day, not on its left edge. It plots a
                    key that isn't in the data, so it can't touch the y domain.

                    It also keeps this chart aligned with the rolls bar card
                    above: both bucket through bucketShiftRolls and key on the
                    bucket start, so a period sits at the same x in each. See
                    AxisAlign.test.tsx.
                  */}
                  <Bar dataKey="__axisSpacer" hide />
                  <CartesianGrid strokeDasharray="3 3" />
                  {/* The A-grade target and the level it should not fall
                      below. Drawn only — nothing fires on either yet. */}
                  <ReferenceLine
                    y={ALERT_A_PCT}
                    stroke="#D32F2F"
                    strokeDasharray="2 4"
                    strokeWidth={1.5}
                    label={{
                      value: `Alert ${ALERT_A_PCT}%`,
                      position: "insideBottomRight",
                      fill: "#D32F2F",
                      fontSize: 11,
                    }}
                  />
                  <ReferenceLine
                    y={GOAL_A_PCT}
                    stroke="#9e9e9e"
                    strokeDasharray="6 4"
                    strokeWidth={1.5}
                    label={{
                      value: `Goal ${GOAL_A_PCT}%`,
                      position: "insideBottomRight",
                      fill: "#757575",
                      fontSize: 11,
                    }}
                  />
                  <XAxis
                    dataKey="shift_id"
                    // Every shift gets a tick: recharts' thinning skips
                    // alternate ones, which on a Day/Night axis hides one shift
                    // type entirely and reads as if only days ran.
                    tick={renderShiftTick(data, true, true, data.length > 12)}
                    height={42}
                    interval={0}
                  />
                  <YAxis
                    width={AXIS_WIDTH}
                    tickMargin={8}
                    // Fixed 0–100 shows the real proportions rather than
                    // magnifying whatever happens to be on screen.
                    domain={[0, 100]}
                    tickFormatter={(v: any) => `${Math.round(Number(v))}%`}
                  />
                  <Tooltip
                    cursor={{ stroke: "rgba(0,0,0,0.2)" }}
                    labelFormatter={(_, payload) => {
                      const p = payload?.[0]?.payload as Point | undefined;
                      if (!p) return "";
                      return `${p.label} · ${p.sublabel}${
                        p.is_partial ? " (in progress)" : ""
                      } — ${fmtRolls(p.total)} rolls`;
                    }}
                    formatter={(v: any, name: any, item: any) => {
                      const p = item?.payload as Point | undefined;
                      const key = item?.dataKey as "a" | "b" | "f";
                      const count = p ? p.counts[key] : 0;
                      return [
                        `${Number(v).toFixed(1)}% (${fmtRolls(count)})`,
                        name,
                      ];
                    }}
                  />
                                      <Line
                      type="linear"
                      dataKey="a"
                      name="A"
                      stroke={ROLL_COLORS.a}
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      activeDot={{ r: 5 }}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  <Line
                    type="linear"
                    dataKey="b"
                    name="B"
                    stroke={ROLL_COLORS.b}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="linear"
                    dataKey="f"
                    name="F"
                    stroke={ROLL_COLORS.f}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </LoadingOverlay>
        )}

        {/* Averages and the partial-period note are real Lam 2 figures; under
            a template heading they would be read as that selection's. */}
        {/* Dashes while loading would read as "no rolls in this window". */}
        {!unavailable && loading && (
          <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
            {[64, 64, 64].map((w, i) => (
              <Skeleton key={i} variant="text" width={w} />
            ))}
          </Stack>
        )}
        {!unavailable && !loading && (
          <Stack
            direction="row"
            spacing={3}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 1 }}
          >
            <Typography variant="caption" color="text.secondary">
              Avg A{" "}
              <b style={{ color: ROLL_COLORS.a }}>
                {avg.a === null ? "—" : `${avg.a.toFixed(1)}%`}
              </b>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Avg B{" "}
              <b style={{ color: ROLL_COLORS.b }}>
                {avg.b === null ? "—" : `${avg.b.toFixed(1)}%`}
              </b>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Avg F{" "}
              <b style={{ color: ROLL_COLORS.f }}>
                {avg.f === null ? "—" : `${avg.f.toFixed(1)}%`}
              </b>
            </Typography>
            {partial && (
              <Typography variant="caption" color="text.secondary">
                The last point ({partial.label}) is the {group} in progress.
              </Typography>
            )}
          </Stack>
        )}
    </Box>
  );
};

export default RollMixPanel;
